import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiClock, FiMapPin, FiFileText, FiCheck, FiSend } from 'react-icons/fi';
import './AddSchedule.css';

const AddSchedule = () => {
  const [sessionName, setSessionName] = useState('');
  const [presentationType, setPresentationType] = useState('');
  const [selectedPapers, setSelectedPapers] = useState([]);
  const [papers, setPapers] = useState([]);
  const [availablePapers, setAvailablePapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [schedule, setSchedule] = useState({
    date: '',
    timeFrom: '',
    timeTo: '',
    room: '',
    venue: ''
  });
  const navigate = useNavigate();

  const venueOptions = [
    "Virtual Classroom, JUST",
    "Jatiya Kabi Kazi Nazrul Islam Academic Building, JUST",
    "Gymnasium, JUST"
  ];

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-accepted-papers`
        );
        
        // Fixed: Access nested data array
        if (response.data?.data) {
          setPapers(response.data.data);
        } else {
          setError('Unexpected data format received from the server.');
        }
      } catch (error) {
        console.error('Error fetching papers:', error);
        setError('Failed to load papers. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPapers();
  }, []);

  useEffect(() => {
    if (presentationType && papers.length > 0) {
      // Case-insensitive filtering
      const filtered = papers.filter(paper => 
        paper.presentationType?.toLowerCase() === presentationType.toLowerCase()
      );
      
      setAvailablePapers(filtered);
      setSelectedPapers([]);
    } else {
      setAvailablePapers([]);
    }
  }, [presentationType, papers]);

  const togglePaperSelection = (paper) => {
    setSelectedPapers(prev => {
      const exists = prev.some(p => p.paperID === paper.paperID);
      if (exists) {
        return prev.filter(p => p.paperID !== paper.paperID);
      } else {
        return [...prev, paper];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedPapers.length === availablePapers.length) {
      setSelectedPapers([]);
    } else {
      setSelectedPapers([...availablePapers]);
    }
  };

  const handleScheduleChange = (field, value) => {
    setSchedule(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sessionName || !presentationType) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete!',
        text: 'Please select both session and presentation type.',
      });
      return;
    }

    if (selectedPapers.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Papers Selected!',
        text: 'Please select at least one paper for scheduling.',
      });
      return;
    }

    // Validate schedule fields (room is now optional)
    if (!schedule.date || !schedule.timeFrom || !schedule.timeTo || !schedule.venue) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Schedule!',
        text: 'Please fill all required schedule fields.',
      });
      return;
    }

    const payload = {
      session: sessionName,
      presentationType,
      schedule: {
        ...schedule,
        papers: selectedPapers.map(paper => paper.paperID)
      }
    };

    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/add-schedule`, 
        payload,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Session schedule created successfully!',
      });

      navigate('/admin/remove-schedule');
    } catch (error) {
      console.error('Error saving schedule:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed!',
        text: error.response?.data?.message || 'Failed to create schedule.',
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCount = () => selectedPapers.length;

  if (loading && !presentationType) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading papers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <p className="error-message">{error}</p>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="add-schedule">
      <div className="email-header">
        <h1><FiCalendar /> Add Session Schedule</h1>
        <p>Create schedules for oral or poster presentation sessions</p>
      </div>

      <div className="form-section">
        <div className="form-group">
          <label><FiFileText /> Session Name</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Technical Session-01, Parallel Session-01"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label><FiFileText /> Presentation Type</label>
          <select
            className="form-control"
            value={presentationType}
            onChange={(e) => setPresentationType(e.target.value)}
            required
          >
            <option value="">Select Presentation Type</option>
            <option value="Oral">Oral Presentation</option>
            <option value="Poster">Poster Presentation</option>
          </select>
        </div>
      </div>

      {presentationType && (
        <div className="schedule-content">
          <div className="recipient-section">
            <div className="section-header">
              <h2><FiFileText /> Available Papers ({presentationType})</h2>
              {!loading && availablePapers.length > 0 && (
                <div className="recipient-count">
                  {getSelectedCount()} of {availablePapers.length} papers selected
                  <button 
                    className="select-all-btn"
                    onClick={toggleSelectAll}
                  >
                    {selectedPapers.length === availablePapers.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              )}
            </div>
            
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading {presentationType.toLowerCase()} papers...</p>
              </div>
            ) : availablePapers.length === 0 ? (
              <div className="empty-state">
                <FiFileText size={48} />
                <h3>No {presentationType} Papers Found</h3>
                <p>There are currently no {presentationType.toLowerCase()} papers to schedule.</p>
              </div>
            ) : (
              <div className="recipients-list">
                {availablePapers.map(paper => (
                  <div 
                    key={paper.paperID}
                    className={`recipient-item ${selectedPapers.some(p => p.paperID === paper.paperID) ? 'selected' : ''}`}
                    onClick={() => togglePaperSelection(paper)}
                  >
                    <div className="recipient-info">
                      <div className="recipient-name">{paper.title}</div>
                      <div className="recipient-email">ID: {paper.paperID}</div>
                    </div>
                    <div className="recipient-checkbox">
                      {selectedPapers.some(p => p.paperID === paper.paperID) && <FiCheck />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="compose-section">
            <div className="section-header">
              <h2><FiCalendar /> Session Schedule</h2>
              {selectedPapers.length > 0 && (
                <p className="selected-count">
                  {selectedPapers.length} paper{selectedPapers.length !== 1 ? 's' : ''} selected for this session
                </p>
              )}
            </div>

            {selectedPapers.length > 0 ? (
              <div className="schedule-form">
                <div className="schedule-fields">
                  <div className="form-group">
                    <label><FiCalendar /> Date</label>
                    <input
                      type="date"
                      value={schedule.date}
                      onChange={(e) => handleScheduleChange('date', e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>
                  
                  <div className="time-fields">
                    <div className="form-group">
                      <label><FiClock /> Start Time</label>
                      <input
                        type="time"
                        value={schedule.timeFrom}
                        onChange={(e) => handleScheduleChange('timeFrom', e.target.value)}
                        required
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label><FiClock /> End Time</label>
                      <input
                        type="time"
                        value={schedule.timeTo}
                        onChange={(e) => handleScheduleChange('timeTo', e.target.value)}
                        required
                        className="form-control"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Room (Optional)</label>
                    <input
                      type="text"
                      placeholder="Room number or name (optional)"
                      value={schedule.room}
                      onChange={(e) => handleScheduleChange('room', e.target.value)}
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label><FiMapPin /> Venue</label>
                    <select
                      value={schedule.venue}
                      onChange={(e) => handleScheduleChange('venue', e.target.value)}
                      required
                      className="form-control"
                    >
                      <option value="">Select Venue</option>
                      {venueOptions.map((venue, idx) => (
                        <option key={idx} value={venue}>{venue}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="selected-papers-list">
                  <h3>Papers in this Session:</h3>
                  <ul>
                    {selectedPapers.map(paper => (
                      <li key={paper.paperID}>
                        <span className="paper-id">#{paper.paperID}</span>
                        <span className="paper-title">{paper.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <FiFileText size={48} />
                <h3>No Papers Selected</h3>
                <p>Select papers from the list to create a session schedule.</p>
              </div>
            )}

            <div className="schedule-actions">
              <button 
                className="send-btn"
                onClick={handleSubmit}
                disabled={loading || selectedPapers.length === 0}
              >
                {loading ? (
                  <span className="sending-spinner"></span>
                ) : (
                  <><FiSend /> Create Session Schedule</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSchedule;